'use client';
/**
 * UTILISATEURS & POLITIQUE DES COMPTES — l'annuaire de la suite.
 *
 * ── D'où vient cet écran (30/08/2026) ──────────────────────────────────────
 *
 * Il se réglait depuis le module Site. Décision de la RQ : le rapatrier dans le
 * paramétrage de la Qualité. C'est le bon endroit — un compte donne accès au
 * système qualité, et c'est la RQ qui répond de qui y entre, pas la personne
 * qui édite la vitrine.
 *
 * ⚠️ LES ROUTES DU MOTEUR N'ONT PAS BOUGÉ. Ce n'est pas une réécriture, c'est un
 * déplacement de la porte d'entrée : mêmes chemins `/api/team/*`, même autorité
 * (Strapi refuse tout ce qui n'est pas super admin). L'écran ne fait que le
 * piloter — et il l'a toujours fait.
 *
 * ⚠️ CE N'EST PAS L'ANNUAIRE DE L'EIL. Depuis l'isolation du 29/08, l'échange
 * inter-laboratoires a SA base et SES comptes ; ceux d'ici ne l'ouvrent pas, et
 * réciproquement. Une même personne peut figurer des deux côtés avec deux
 * identifiants distincts.
 *
 * ── Les trois distinctions que cet écran refuse de perdre ──────────────────
 *
 *  · « JAMAIS CONNECTÉ » ≠ « DORMANT ». Le premier n'a pas encore commencé, le
 *    second s'est arrêté. Un compte jamais connecté se juge sur sa date de
 *    création : une invitation récente n'est pas dormante.
 *  · « SUSPENDU » ≠ « DORMANT ». Le premier est inactif par DÉCISION, le second
 *    par oubli. Signaler le premier serait du bruit.
 *  · « SANS PERSONNE » N'EST PAS AFFIRMÉ TANT QUE L'ORGANIGRAMME N'A PAS ÉTÉ LU.
 *    Approximer un rapprochement qu'on n'a pas pu faire accuserait des comptes
 *    parfaitement légitimes, sur l'écran même d'où l'on suspend.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { I, card, useConfirmation } from '@/components/ui';
import { PROFILS_COMPTES, type ProfilOption } from '@/lib/profils-comptes';
import { fetchEcriture } from '@/lib/ecrire';
import { useGed } from '@/lib/store';
import {
  listUsers, inviteUser, updateUserProfile, setUserApps, resendInvite, setUserStatus,
  defaultApps, moisDInactivite,
  statutDetaille, expirationConnue, envoyerLienReinitialisation, listPersonnesOrganigramme,
  updateUserLibelle, updateUserCorrespondant,
  type TeamUser, type AppAccess, type StatutDetaille, type PersonneOrg,
  type VerdictEnvoi as VerdictCourriel,
} from '@/lib/team';
/* ⚠️ LES DEMANDES D'INSCRIPTION ONT QUITTÉ CE FICHIER le 13/09/2026 — elles
   n'ont pas été supprimées, elles ont été COMPLÉTÉES ailleurs. Ce qui vivait
   ici n'en était qu'une moitié : les demandes EN ATTENTE, deux boutons, rien
   d'autre. Les demandes déjà traitées — 6 en base, toutes refusées — n'étaient
   visibles que dans l'inbox du site, avec leur suivi et leur journal. C'est
   cette seconde porte que l'arbitrage du 13/09 referme, et refermer une porte
   sans emporter ce qu'elle donnait aurait retiré une capacité en silence. */
import DemandesCompte from './DemandesCompte';
import { ChoixCorrespondant } from '@/components/ChoixCorrespondant';

const MODULES: { key: keyof AppAccess; short: string; label: string }[] = [
  { key: 'site', short: 'S', label: 'Site' },
  { key: 'manuel', short: 'M', label: 'Manuel' },
  { key: 'ged', short: 'Q', label: 'Qualité' },
];

const STATUTS: { key: StatutDetaille; label: string; style: React.CSSProperties }[] = [
  { key: 'actif', label: 'Actif', style: { background: 'var(--green-50)', color: 'var(--green-600)' } },
  { key: 'jamais', label: 'Jamais connecté', style: { background: 'var(--cyan-50)', color: 'var(--cyan-700)' } },
  { key: 'dormant', label: 'Dormant', style: { background: 'var(--amber-50)', color: 'var(--amber-600)' } },
  { key: 'invite', label: 'Invité', style: { background: 'var(--amber-50)', color: 'var(--amber-600)' } },
  { key: 'expiree', label: 'Invitation expirée', style: { background: 'var(--red-50)', color: 'var(--red-600)' } },
  { key: 'suspended', label: 'Suspendu', style: { background: 'var(--amber-50)', color: 'var(--amber-600)' } },
  { key: 'archived', label: 'Archivé', style: { background: 'var(--gray-100)', color: 'var(--text-muted)' } },
];

const STATUT_LABEL = Object.fromEntries(STATUTS.map((s) => [s.key, s.label])) as Record<StatutDetaille, string>;
const STATUT_STYLE = Object.fromEntries(STATUTS.map((s) => [s.key, s.style])) as Record<StatutDetaille, React.CSSProperties>;

/** Valeur de filtre supplémentaire, qui n'est pas un statut de compte mais un
 *  ÉCART avec l'organigramme du module Qualité. */
const SANS_PERSONNE = 'sans-personne';

const lbl: React.CSSProperties = { display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 6 };
const field: React.CSSProperties = { width: '100%', height: 38, border: '1.5px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '0 11px', fontSize: 13.5, color: 'var(--text-strong)', background: 'var(--surface-card)', fontFamily: 'var(--font-sans)' };

function SectionTitle({ children, note }: { children: React.ReactNode; note?: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <span style={{ width: 8, height: 8, borderRadius: 'var(--radius-xs)', background: 'var(--color-primary)', flex: 'none' }} />
        <h3 style={{ margin: 0, fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-strong)' }}>{children}</h3>
      </div>
      {note && <p style={{ margin: '7px 0 0', fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-muted)' }}>{note}</p>}
    </div>
  );
}

/* ---------------- Utilisateurs (commun à la suite) ---------------- */

/**
 * Compte dormant = aucune connexion depuis le seuil paramétré (Politique des
 * comptes). Les comptes déjà suspendus ou archivés en sont exclus : ils sont
 * inactifs par décision, pas par oubli — les signaler serait du bruit.
 */
function estDormant(u: TeamUser, seuilMois: number): boolean {
  return statutDetaille(u, seuilMois) === 'dormant';
}

/** « Dernière connexion : il y a 4 mois » — ou l'absence de connexion. */
function derniereConnexion(u: TeamUser): string {
  if (!u.lastLoginAt) {
    return u.status === 'pending' ? 'Invitation en attente — jamais connecté' : 'Jamais connecté';
  }
  const m = moisDInactivite(u) ?? 0;
  if (m < 1) {
    const jours = Math.max(0, Math.round(m * 30.4375));
    return `Dernière connexion : ${jours <= 1 ? 'aujourd’hui' : `il y a ${jours} jours`}`;
  }
  const mois = Math.round(m);
  return `Dernière connexion : il y a ${mois} mois`;
}

/**
 * LE NOM D'UN COMPTE SE CORRIGE À UN SEUL ENDROIT (03/10/2026, décision RQ).
 *
 * Compte RATTACHÉ à une personne : son nom est celui de la fiche du personnel
 * (titre + prénom + nom), il n'y a rien à saisir ici. Offrir le champ quand
 * même ferait une SECONDE PORTE D'ÉCRITURE sur la même information, et deux
 * écritures de la même chose divergent toujours — c'est déjà arrivé : le compte
 * `noureddine`, qui est celui de Nour Eddine Abid, portait « S. Trabelsi ».
 *
 * Compte SANS personne (un laboratoire confrère, un compte de recette) : il n'a
 * pas d'identité à emprunter, seulement un LIBELLÉ, qui est bien à lui et se
 * saisit ici.
 *
 * ⚠️ `libelleModifiable` absent = moteur antérieur à cette décision : on
 * n'offre alors pas la saisie plutôt que de la deviner.
 */
function LibelleCompte({ u, onSave }: { u: TeamUser; onSave: (valeur: string) => void }) {
  const [v, setV] = useState(u.libelle ?? '');
  // Un rechargement de la liste doit reprendre la main sur la saisie locale,
  // sinon la ligne afficherait indéfiniment ce qu'on a tapé et non ce qui est
  // enregistré. Recalé PENDANT LE RENDU (motif de `ui.tsx`), pas dans un effet.
  const [libelleVu, setLibelleVu] = useState(u.libelle);
  if (libelleVu !== u.libelle) { setLibelleVu(u.libelle); setV(u.libelle ?? ''); }

  if (u.personne) {
    return (
      <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 2 }}>
        Nom lu sur la fiche du personnel — il se corrige au dossier RH, pas ici.
      </div>
    );
  }
  if (u.libelleModifiable !== true) return null;

  const modifie = v.trim() !== (u.libelle ?? '').trim();
  return (
    <div style={{ marginTop: 5, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>
        Libellé du compte
      </span>
      <input
        value={v}
        onChange={(e) => setV(e.target.value)}
        placeholder={u.username || '— aucun —'}
        aria-label="Libellé du compte"
        style={{ ...field, width: 210, height: 30, fontSize: 12.5 }}
      />
      {modifie && (
        <button type="button" onClick={() => onSave(v.trim())} className="ged-btn"
          style={{ height: 30, padding: '0 10px', fontSize: 12, borderRadius: 'var(--radius-md)', border: '1px solid var(--color-primary)', background: 'var(--color-primary)', color: 'var(--text-on-primary)', cursor: 'pointer' }}>
          Enregistrer
        </button>
      )}
    </div>
  );
}

type SortCol = 'name' | 'profile' | 'status';
type Sort = { col: SortCol; dir: 1 | -1 };

function SortHead({ col, label, sort, onSort }: { col: SortCol; label: string; sort: Sort; onSort: (c: SortCol) => void }) {
  const on = sort.col === col;
  return (
    <button onClick={() => onSort(col)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '11px 14px', fontFamily: 'var(--font-sans)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: on ? 'var(--text-strong)' : 'var(--text-muted)', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'start' }}>
      {label}{on && <span style={{ fontSize: 9 }}>{sort.dir === 1 ? '▲' : '▼'}</span>}
    </button>
  );
}

function UsersPanel({ seuilMois }: { seuilMois: number }) {
  /* ⚠️ UNE SEULE LISTE DE PROFILS pour tout le paramétrage (`profils-comptes`).
     Le site tenait la sienne dans son magasin ; la recopier ici aurait fait
     deux listes qui divergent au premier profil ajouté. */
  const profiles = PROFILS_COMPTES;
  const { demander, demanderTexte, confirmation } = useConfirmation();
  const [users, setUsers] = useState<TeamUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  // Ni succès ni échec : l'acte a eu lieu, son effet est incomplet (cf. le
  // rendu plus bas et `envoyerLienReinitialisation`).
  const [alerte, setAlerte] = useState<string | null>(null);
  // `null` = organigramme non lu (droit refusé, module non initialisé…) : le
  // rapprochement est alors TU, pas approximé.
  const [personnes, setPersonnes] = useState<PersonneOrg[] | null>(null);

  // filtres / tri
  const [q, setQ] = useState('');
  const [fProfile, setFProfile] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [fModule, setFModule] = useState('');
  const [sort, setSort] = useState<Sort>({ col: 'name', dir: 1 });

  // invitation
  const [email, setEmail] = useState('');
  const [invProfile, setInvProfile] = useState('technician');

  // `loading` démarre à vrai : un rechargement ultérieur (après une action) se
  // fait sans revenir à l'écran « Chargement… », qui viderait la table sous les
  // yeux de l'utilisateur.
  const reload = React.useCallback(() => {
    listUsers().then((l) => { setUsers(l); setError(null); })
      .catch((e) => setError(e instanceof Error ? e.message : 'Erreur serveur'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    reload();
    void listPersonnesOrganigramme().then(setPersonnes);
  }, [reload]);

  const labelOf = (p: string) => profiles.find((o: ProfilOption) => o.value === p)?.label || p;

  /* Ouvrir le dossier d'une personne depuis l'annuaire : cet écran vit dans la
     fenêtre de paramétrage, qu'il faut REFERMER — sinon l'écran de destination
     s'ouvre derrière elle, invisible. `selectedPersonne` est la clé que l'écran
     d'arrivée consomme pour ouvrir le dossier (même geste que l'intégration RH).

     ⚠️ CAP CHANGÉ LE 06/10/2026 : « DOSSIERS RH » ET NON L'ORGANIGRAMME. Celui-ci
     n'ouvre plus qu'un résumé en lecture seule ; y envoyer quelqu'un qui vient
     chercher le dossier d'un compte l'aurait fait arriver sur un panneau sans
     aucun champ, à un clic de plus de ce qu'il cherchait. */
  const { closeSettings, naviguerVers } = useGed();
  const ouvrirDossier = (orgId: string) => {
    closeSettings();
    naviguerVers('dossiers', { cle: 'selectedPersonne', valeur: orgId });
  };

  /**
   * Un compte est « sans personne » quand son identifiant ne figure PAS à
   * l'organigramme du module Qualité. Tant que l'organigramme n'a pas été lu,
   * la réponse est `false` pour tout le monde — on ne montre pas d'écart qu'on
   * n'a pas pu constater.
   */
  const idsOrg = useMemo(() => (personnes ? new Set(personnes.map((p) => p.id)) : null), [personnes]);
  const sansPersonne = React.useCallback(
    (u: TeamUser) => !!idsOrg && !!(u.cle || u.username) && !idsOrg.has(u.cle || u.username!),
    [idsOrg],
  );

  const statutDe = React.useCallback((u: TeamUser) => statutDetaille(u, seuilMois), [seuilMois]);

  const compte = useMemo(() => {
    const c: Record<string, number> = { [SANS_PERSONNE]: 0 };
    for (const u of users) {
      const s = statutDe(u);
      c[s] = (c[s] || 0) + 1;
      if (sansPersonne(u)) c[SANS_PERSONNE] += 1;
    }
    return c;
  }, [users, statutDe, sansPersonne]);

  const shown = useMemo(() => {
    const needle = q.toLowerCase().trim();
    const list = users.filter((u) => {
      /* La recherche porte sur le nom RÉELLEMENT AFFICHÉ (`u.name`, résolu par
         le moteur depuis la fiche RH), plus le libellé brut : chercher
         « Trabelsi » ne doit plus ramener le compte de Nour Eddine Abid. */
      if (needle && !`${u.name} ${u.libelle ?? ''} ${u.email ?? ''} ${u.username ?? ''} ${u.personne?.nomAffiche ?? ''} ${u.personne?.numero ?? ''}`.toLowerCase().includes(needle)) return false;
      if (fProfile && u.profile !== fProfile) return false;
      if (fModule && !(u.apps || defaultApps(u.profile))[fModule as keyof AppAccess]) return false;
      if (fStatus === SANS_PERSONNE) return sansPersonne(u);
      if (fStatus && statutDe(u) !== fStatus) return false;
      return true;
    });
    return [...list].sort((a, b) => {
      // `u.name` EST le nom affiché dans la colonne : trier dessus et afficher
      // autre chose ferait une liste qui semble mal rangée.
      const val = (u: TeamUser) => sort.col === 'name' ? u.name : sort.col === 'profile' ? labelOf(u.profile) : STATUT_LABEL[statutDe(u)];
      return val(a).localeCompare(val(b)) * sort.dir;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, q, fProfile, fStatus, fModule, sort, profiles, statutDe, sansPersonne]);

  const toggleSort = (col: SortCol) => setSort((s) => (s.col === col ? { col, dir: (s.dir === 1 ? -1 : 1) } : { col, dir: 1 }));

  // Toutes les actions comptes sont IMMÉDIATES (elles partent au serveur) —
  // le pied « Enregistrer » ne concerne que l'identité et les couleurs.
  // `alerte` est effacée par toute action suivante : une bande orange laissée
  // en place se rapporterait à un compte qu'on ne regarde plus.
  const run = (p: Promise<unknown>) => p.then(() => { setError(null); setAlerte(null); reload(); })
    .catch((e) => setError(e instanceof Error ? e.message : 'Action refusée'));

  /**
   * Annonce le résultat d'un acte QUI DÉCLENCHE UN COURRIEL.
   *
   * L'acte a produit son effet avant l'envoi (compte créé, jeton remplacé,
   * demande approuvée, sessions fermées) : le succès de l'appel ne dit donc
   * rien de ce que la personne a reçu. Vert seulement si le message est parti ;
   * sinon la bande orange, qui nomme les deux faits — ce qui a eu lieu, et ce
   * qui n'a pas eu lieu.
   */
  const annoncerEnvoi = (r: VerdictCourriel, succes: string, echec: (motif: string) => string) => {
    setError(null);
    if (r.courrielEnvoye) { setAlerte(null); setInfo(succes); }
    else { setInfo(null); setAlerte(echec(r.motifEchec || 'motif inconnu')); }
  };

  const inviter = () => {
    const adresse = email.trim();
    if (!adresse) return;
    setInfo(null); setAlerte(null);
    void inviteUser(adresse, invProfile)
      .then((r) => {
        setEmail('');
        reload();
        annoncerEnvoi(r, `Invitation envoyée à ${adresse}.`, (m) =>
          `Le compte de ${adresse} a bien été créé et attend son invitation — mais le courriel n’est PAS parti (${m}). `
          + `La personne n’a rien reçu et ne peut rien faire. Relancez « Renvoyer l’invitation » une fois le service de `
          + `courriel rétabli ; le lien en cours reste valable 48 h et figure dans le journal du serveur.`);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Action refusée'));
  };

  const renvoyerInvitation = (u: TeamUser) => {
    setInfo(null); setAlerte(null);
    void resendInvite(u.id)
      .then((r) => {
        reload();
        annoncerEnvoi(r, `Invitation renvoyée à ${u.email}.`, (m) =>
          `L’invitation de ${u.email} a été renouvelée — mais le courriel n’est PAS parti (${m}). `
          + `⚠️ Le lien précédent est désormais mort et le nouveau n’est arrivé nulle part : la personne est dans un état `
          + `pire qu’avant ce clic. Relancez l’envoi une fois le service de courriel rétabli ; le lien en cours reste `
          + `valable 48 h et figure dans le journal du serveur.`);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Action refusée'));
  };

  /**
   * Envoi d'un LIEN de réinitialisation. Le message de confirmation dit
   * exactement ce qui se passe, parce que c'est la garantie qui compte : cet
   * écran n'engendre, n'affiche et ne saisit AUCUN mot de passe.
   */
  const reinitialiser = async (u: TeamUser) => {
    if (!u.email) { setError('Ce compte n’a pas d’adresse e-mail : aucun lien ne peut lui être envoyé.'); return; }
    if (!(await demander(
      `Envoyer un lien de réinitialisation à ${u.email} ?\n\n`
      + `La personne choisira elle-même son nouveau mot de passe sur la page sécurisée, comme à l’inscription. `
      + `Le lien expire dans 48 heures.\n\n`
      + `Aucun mot de passe n’est engendré, affiché ni transmis ici : vous ne connaîtrez pas le sien.\n\n`
      + `Ses sessions ouvertes seront fermées immédiatement, et ses appareils de confiance oubliés `
      + `(le code à usage unique lui sera redemandé) : si le mot de passe actuel est compromis, `
      + `le laisser vivre une session ouverte n’aurait rien fermé.`,
      { titre: 'Envoyer un lien de réinitialisation', confirmer: 'Envoyer' },
    ))) return;
    setInfo(null); setAlerte(null);
    void envoyerLienReinitialisation(u.id)
      .then((r) => {
        setError(null);
        if (r.courrielEnvoye) {
          // On dit AUSSI ce que le geste a coupé : la personne va être
          // déconnectée de ses sessions ouvertes, et l'administrateur doit
          // pouvoir le lui annoncer plutôt que de le lui faire découvrir.
          setInfo(`Lien de réinitialisation envoyé à ${u.email}. Ses sessions ouvertes ont été fermées ; elle se reconnectera après avoir choisi son nouveau mot de passe.`);
          return;
        }
        // Le cas qui mentait avant : 200 côté serveur, courriel jamais parti.
        // On nomme les DEUX faits — ce qui a eu lieu (irréversible) et ce qui
        // n'a pas eu lieu — puis on donne la seule suite utile. Sans cela
        // l'administrateur croit la personne prévenue, et la personne se
        // découvre dehors sans savoir pourquoi.
        setAlerte(
          `${u.name || u.email} a bien été déconnectée de ses sessions et ses appareils de confiance sont oubliés — `
          + `mais le courriel n’est PAS parti (${r.motifEchec}). Elle n’a donc pas été prévenue et ne peut plus se connecter. `
          + `Prévenez-la et relancez l’envoi une fois le service de courriel rétabli ; le lien en cours reste valable 48 h `
          + `et figure dans le journal du serveur.`,
        );
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Action refusée'));
  };

  const toggleApp = (u: TeamUser, key: keyof AppAccess) => {
    const cur = u.apps || defaultApps(u.profile);
    void run(setUserApps(u.id, { ...cur, [key]: !cur[key] }));
  };

  const dormants = users.filter((u) => estDormant(u, seuilMois));
  const orphelins = users.filter(sansPersonne);
  const filtersOn = !!(q || fProfile || fStatus || fModule);
  // « Invitation expirée » n'est proposée que si le serveur donne l'échéance
  // du jeton : une entrée qui afficherait toujours 0 ferait croire qu'aucune
  // invitation n'est morte, ce qui serait faux.
  const statutsProposes = STATUTS.filter((s) => s.key !== 'expiree' || expirationConnue(users));

  return (
    <div>
      <SectionTitle note={<>Comptes de la suite BIO24 : le profil et les accès par module valent pour le site, le manuel et la Qualité. Les modifications ci-dessous sont <strong>immédiates</strong>.</>}>Utilisateurs</SectionTitle>

      {error && (
        <div style={{ marginBottom: 14, padding: '10px 13px', borderRadius: 'var(--radius-md)', background: 'var(--red-50)', color: 'var(--red-600)', fontSize: 12.5, fontWeight: 600 }}>{error}</div>
      )}
      {info && (
        <div style={{ marginBottom: 14, padding: '10px 13px', borderRadius: 'var(--radius-md)', background: 'var(--green-50)', color: 'var(--green-600)', fontSize: 12.5, fontWeight: 600 }}>{info}</div>
      )}
      {/* Troisième état, ni vert ni rouge : l'acte a EU LIEU mais il n'a pas
          produit tout son effet. Le peindre en vert mentirait, en rouge ferait
          croire que rien ne s'est passé — et ferait recommencer un geste qui a
          déjà fermé les sessions de quelqu'un. `role="alert"` parce que c'est
          la seule annonce de l'écran qui demande une action hors écran. */}
      {alerte && (
        <div role="alert" style={{ marginBottom: 14, padding: '10px 13px', borderRadius: 'var(--radius-md)', background: 'var(--amber-50)', color: 'var(--amber-700)', border: '1px solid var(--amber-200)', fontSize: 12.5, fontWeight: 600, lineHeight: 1.5 }}>{alerte}</div>
      )}

      {/* Comptes sans personne à l'organigramme — l'écart que ni l'un ni
          l'autre des deux écrans ne montrait seul. Aucune action n'est prise
          automatiquement : le sort de ces comptes est une décision du
          laboratoire, l'écran se contente de la rendre possible. */}
      {orphelins.length > 0 && (
        <div style={{ marginBottom: 16, padding: '12px 14px', borderRadius: 'var(--radius-md)', background: 'var(--red-50)', border: '1px solid #F1C9C7', color: 'var(--red-600)', fontSize: 12.5, lineHeight: 1.55 }}>
          <strong>{orphelins.length} compte(s) sans personne à l’organigramme</strong> — un accès que plus aucun dossier ne revendique :
          <ul style={{ margin: '6px 0 0', paddingInlineStart: 20 }}>
            {orphelins.map((u) => (
              <li key={u.id}>
                <strong>{u.username || u.name}</strong> · {u.email || 'sans e-mail'} · {STATUT_LABEL[statutDe(u)].toLowerCase()} · {derniereConnexion(u).toLowerCase()}
              </li>
            ))}
          </ul>
          <div style={{ marginTop: 6 }}>
            Rapprochez chaque ligne d’une personne (module Qualité → Organigramme), ou suspendez / archivez le compte
            avec les boutons de sa ligne. Rien n’est fait automatiquement.
          </div>
        </div>
      )}

      {dormants.length > 0 && (
        <div style={{ marginBottom: 16, padding: '11px 14px', borderRadius: 'var(--radius-md)', background: 'var(--amber-50)', border: '1px solid var(--amber-600)', color: 'var(--amber-600)', fontSize: 12.5, lineHeight: 1.5 }}>
          <strong>{dormants.length} compte(s) sans connexion depuis plus de {seuilMois} mois</strong> : {dormants.map((u) => u.name).join(', ')}.
          <br />Suspendez-les (absence) ou archivez-les (départ) — et pensez à transférer leurs responsabilités depuis le module Qualité.
        </div>
      )}

      {/* Les demandes d'inscription vivent désormais dans leur propre bloc,
          qui est la SEULE porte pour les traiter (arbitrage RQ du 13/09/2026).
          Ce qui se trouvait ici n'en montrait que la moitié — les demandes en
          attente, sans historique, sans suivi et sans journal. */}
      <DemandesCompte onCompteChange={reload} />


      {/* ---- filtres ---- */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 190 }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', insetInlineStart: 12, top: '50%', transform: 'translateY(-50%)' }}><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher (nom, e-mail, identifiant, P-000n)…" style={{ ...field, padding: '0 34px 0 34px' }} />
          {/* Croix d'effacement — `type="search"` en offre une, mais WebKit ne
              la montre qu'au survol et Firefox pas du tout : la commande
              existerait sur un navigateur et pas sur l'autre. On la dessine. */}
          {q.length > 0 && (
            <button type="button" onClick={() => setQ('')} title="Effacer la recherche" aria-label="Effacer la recherche"
              style={{ position: 'absolute', insetInlineEnd: 8, top: '50%', transform: 'translateY(-50%)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, padding: 0, border: 'none', borderRadius: '50%', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>
              ×
            </button>
          )}
        </div>
        <select value={fProfile} onChange={(e) => setFProfile(e.target.value)} style={{ ...field, width: 'auto', minWidth: 150 }}>
          <option value="">Tous les profils</option>
          {profiles.map((o: ProfilOption) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} style={{ ...field, width: 'auto', minWidth: 130 }}>
          {/* Chaque entrée porte son EFFECTIF RÉEL : une catégorie à zéro se
              voit, elle ne se devine pas après un clic sur une liste vide. */}
          <option value="">Tous les statuts ({users.length})</option>
          {statutsProposes.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}{s.key === 'dormant' ? ` (> ${seuilMois} mois)` : ''} — {compte[s.key] || 0}
            </option>
          ))}
          {idsOrg && <option value={SANS_PERSONNE}>Sans personne à l’organigramme — {compte[SANS_PERSONNE] || 0}</option>}
        </select>
        <select value={fModule} onChange={(e) => setFModule(e.target.value)} style={{ ...field, width: 'auto', minWidth: 150 }}>
          <option value="">Tous les modules</option>
          {MODULES.map((m) => <option key={m.key} value={m.key}>Accès {m.label}</option>)}
        </select>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--text-body)' }}>{shown.length}</span> / {users.length}
        </span>
        {filtersOn && (
          <button onClick={() => { setQ(''); setFProfile(''); setFStatus(''); setFModule(''); }}
            style={{ background: 'transparent', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', padding: '6px 11px', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer', fontFamily: 'var(--font-sans)' }}>Réinitialiser</button>
        )}
      </div>

      {/* ---- table ---- */}
      <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflowX: 'auto' }}>
        <div style={{ minWidth: 700 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 168px 132px 96px 104px', background: 'var(--gray-25)', borderBottom: '1px solid var(--border-default)' }}>
            <SortHead col="name" label="Utilisateur" sort={sort} onSort={toggleSort} />
            <SortHead col="profile" label="Profil attribué" sort={sort} onSort={toggleSort} />
            <span style={{ display: 'inline-flex', alignItems: 'center', padding: '10px 14px', fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>Accès module</span>
            <SortHead col="status" label="Statut" sort={sort} onSort={toggleSort} />
            <div />
          </div>

          {loading && users.length === 0 && <div style={{ padding: '16px 14px', fontSize: 13, color: 'var(--text-muted)' }}>Chargement…</div>}
          {!loading && shown.length === 0 && (
            <div style={{ padding: '16px 14px', fontSize: 13, color: 'var(--text-muted)' }}>
              {users.length === 0 ? 'Aucun utilisateur. Invitez le premier ci-dessous.' : 'Aucun utilisateur ne correspond à ces filtres.'}
            </div>
          )}

          {shown.map((u) => (
            <div key={u.id} style={{ display: 'grid', gridTemplateColumns: '1fr 168px 132px 96px 104px', alignItems: 'center', borderTop: '1px solid var(--border-subtle)' }}>
              <div style={{ padding: '10px 14px' }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-strong)' }}>
                  {u.name}
                  {sansPersonne(u) && (
                    /* Une fiche RH peut exister SANS être à l'organigramme (sortie,
                       ou retirée à la main) : le badge le dit au lieu d'affirmer
                       qu'il n'y a « personne » — la ligne du dessous la nomme. */
                    <span title={u.personne
                      ? `La fiche de ${u.personne.nomAffiche} existe au dossier du personnel, mais elle n’est plus à l’organigramme`
                      : 'Aucune personne de l’organigramme ne porte cet identifiant'}
                    style={{ marginInlineStart: 7, display: 'inline-block', fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 'var(--radius-pill)', background: 'var(--red-50)', color: 'var(--red-600)' }}>
                      {u.personne ? 'hors organigramme' : 'sans personne'}
                    </span>
                  )}
                </div>
                {u.email && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{u.email}{u.phone ? ` · ${u.phone}` : ''}</div>}
                {/* QUI EST DERRIÈRE CE COMPTE (03/10/2026, demande RQ) — et ce
                    qu'on tape pour s'y connecter. L'identifiant n'apparaissait nulle
                    part sur cet écran : on ne voyait que le nom et l'adresse. */}
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, display: 'flex', flexWrap: 'wrap', gap: '2px 10px' }}>
                  {u.username && (
                    <span>Identifiant de connexion : <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-strong)' }}>{u.username}</span></span>
                  )}
                  {u.personne && (
                    <span>
                      Personne (source du nom) :{' '}
                      <button type="button" onClick={() => ouvrirDossier(u.personne!.orgId)}
                        title={`Ouvrir le dossier de ${u.personne.nomAffiche} — c’est là que son nom se corrige`}
                        style={{ padding: 0, border: 'none', background: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit', fontWeight: 600, color: 'var(--color-primary)', textDecoration: 'underline', textUnderlineOffset: 2 }}>
                        {u.personne.nomAffiche} →
                      </button>
                      {u.personne.numero && <span style={{ fontFamily: 'var(--font-mono)' }}> · {u.personne.numero}</span>}
                    </span>
                  )}
                </div>
                <LibelleCompte u={u} onSave={(valeur) => void run(updateUserLibelle(u.id, valeur))} />
                <div style={{ fontSize: 11.5, marginTop: 2, color: estDormant(u, seuilMois) ? 'var(--amber-600)' : 'var(--text-faint)' }}>
                  {derniereConnexion(u)}
                </div>
              </div>
              <div style={{ padding: '8px 14px' }}>
                <select value={u.profile} onChange={(e) => void run(updateUserProfile(u.id, e.target.value))} style={{ ...field, height: 34, fontSize: 12.5 }}>
                  {profiles.map((o: ProfilOption) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                {/* QUI EST CE CORRESPONDANT ? (Ines, 16/10/2026) Un compte
                    Correspondant se relie à une ligne du carnet ; plusieurs
                    comptes peuvent viser la même ligne. C'est la ligne qui
                    décide de ce qu'il lit au catalogue : un laboratoire voit
                    les prix de sous-traitance, un médecin ou une clinique non.
                    Non relié = le moins ouvert. */}
                {u.profile === 'partner' && u.correspondantCode !== undefined && (
                  <div style={{ marginTop: 6 }}>
                    <ChoixCorrespondant id={`correspondant-${u.id}`} valeur={u.correspondantCode || ''}
                      onChange={(code) => void run(updateUserCorrespondant(u.id, code))}
                      vide="— non relié à l’annuaire"
                      aide={u.correspondantCode ? 'Relié : lit le catalogue selon le type de ce correspondant.' : 'Non relié : lit le catalogue comme un correspondant sans prix.'} />
                  </div>
                )}
              </div>
              <div style={{ padding: '8px 10px', display: 'inline-flex', gap: 5 }}>
                {MODULES.map((m) => {
                  const on = (u.apps || defaultApps(u.profile))[m.key];
                  return (
                    <button key={m.key} onClick={() => toggleApp(u, m.key)} title={`${m.label} : ${on ? 'activé' : 'désactivé'}`}
                      style={{ width: 26, height: 26, borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 11.5, fontWeight: 700, border: `1px solid ${on ? 'var(--cyan-500)' : 'var(--border-default)'}`, background: on ? 'var(--cyan-500)' : 'var(--surface-card)', color: on ? 'var(--surface-card)' : 'var(--text-faint)' }}>
                      {m.short}
                    </button>
                  );
                })}
              </div>
              <div style={{ padding: '8px 14px' }}>
                <span title={u.statusReason || undefined} style={{ display: 'inline-block', fontSize: 11.5, fontWeight: 600, padding: '3px 9px', borderRadius: 'var(--radius-pill)', ...STATUT_STYLE[statutDe(u)] }}>
                  {STATUT_LABEL[statutDe(u)]}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                {/* Réinitialisation d'un compte DÉJÀ actif : un lien, jamais un
                    mot de passe. Le même mécanisme d'invitation (jeton 48 h)
                    sert dans les deux cas — un compte encore « invité » se
                    relance avec « Renvoyer l'invitation », juste à côté. */}
                {u.status === 'active' && (
                  <button onClick={() => reinitialiser(u)} title="Envoyer un lien de réinitialisation du mot de passe"
                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-default)', background: 'var(--surface-card)', color: 'var(--cyan-700)', cursor: 'pointer' }}>
                    <I n="mail" s={13} sw={2.2} />
                  </button>
                )}
                {u.status === 'pending' && (
                  <button onClick={() => renvoyerInvitation(u)} title="Renvoyer l’invitation"
                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-default)', background: 'var(--surface-card)', color: 'var(--color-primary)', cursor: 'pointer' }}>
                    <I n="refresh" s={13} sw={2.2} />
                  </button>
                )}
                {/* Cycle de vie plutôt que suppression : un compte supprimé
                    ferait disparaître l'auteur des documents et des signatures.
                    Suspendre (absence longue) et archiver (départ) bloquent la
                    connexion sans toucher à la traçabilité. */}
                {u.status !== 'archived' && u.status !== 'suspended' && (
                  <button onClick={async () => {
                    const r = await demanderTexte('Suspendre ce compte (absence longue).\nMotif / durée prévue :',
                      { titre: 'Suspendre le compte', confirmer: 'Suspendre', min: 0 });
                    if (r !== null) void run(setUserStatus(u.id, 'suspended', r));
                  }} title="Suspendre (absence > 3 mois)"
                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-default)', background: 'var(--surface-card)', color: 'var(--amber-600)', cursor: 'pointer' }}>
                    <I n="clock" s={13} />
                  </button>
                )}
                {u.status !== 'archived' ? (
                  <button onClick={async () => {
                    if (!(await demander(`Archiver le compte de ${u.name} (départ de l’établissement) ?\n\nLa connexion sera bloquée. Pensez à transférer ses responsabilités documentaires depuis le module Qualité → Organigramme avant d’archiver.`,
                      { titre: 'Archiver le compte', confirmer: 'Archiver' }))) return;
                    void run(setUserStatus(u.id, 'archived', 'Départ de l’établissement'));
                  }} title="Archiver (départ de l’établissement)"
                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-default)', background: 'var(--surface-card)', color: 'var(--text-muted)', cursor: 'pointer' }}>
                    <I n="archive" s={13} />
                  </button>
                ) : (
                  <button onClick={async () => { if (await demander(`Réactiver le compte de ${u.name} ?`, { titre: 'Réactiver le compte', confirmer: 'Réactiver' })) void run(setUserStatus(u.id, 'active')); }} title="Réactiver"
                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-default)', background: 'var(--surface-card)', color: 'var(--green-600)', cursor: 'pointer' }}>
                    <I n="refresh" s={13} />
                  </button>
                )}
                {u.status === 'suspended' && (
                  <button onClick={async () => { if (await demander(`Réactiver le compte de ${u.name} ?`, { titre: 'Réactiver le compte', confirmer: 'Réactiver' })) void run(setUserStatus(u.id, 'active')); }} title="Réactiver"
                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-default)', background: 'var(--surface-card)', color: 'var(--green-600)', cursor: 'pointer' }}>
                    <I n="refresh" s={13} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ---- invitation ---- */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 14, flexWrap: 'wrap' }}>
        <input type="email" dir="ltr" value={email} onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') inviter(); }}
          placeholder="email@laboratoire.tn" style={{ ...field, flex: 1, minWidth: 200, width: 'auto' }} />
        <select value={invProfile} onChange={(e) => setInvProfile(e.target.value)} style={{ ...field, width: 'auto', minWidth: 150 }}>
          {profiles.map((o: ProfilOption) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <button onClick={inviter}
          style={{ background: 'var(--color-primary)', color: 'var(--text-on-primary)', border: 'none', borderRadius: 'var(--radius-md)', padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-sans)' }}>Inviter</button>
      </div>
      {confirmation}
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════
 * LES DEUX ONGLETS DU PARAMÉTRAGE
 * ═══════════════════════════════════════════════════════════════════════════ */

/** L'annuaire. Le seuil de dormance vient de la POLITIQUE : il n'est pas
 *  redevinÉ ici, sinon les deux écrans se contrediraient. */
export function OngletUtilisateurs() {
  const [seuil, setSeuil] = useState<number | null>(null);
  useEffect(() => {
    void fetch('/api/politique', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      /* ⚠️ Un échec de lecture retombe sur le défaut du SERVEUR (3 mois) et non
         sur zéro : un seuil à zéro déclarerait TOUT LE MONDE dormant, sur
         l'écran d'où l'on suspend des comptes. */
      .then((d) => setSeuil(Number(d?.inactivityMonths) || 3))
      .catch(() => setSeuil(3));
  }, []);
  if (seuil === null) {
    /* ⚠️ On attend le seuil avant de peindre la table. L'afficher d'abord avec
       une valeur provisoire ferait clignoter des pastilles « dormant » qui
       disparaîtraient ensuite — et on aurait vu un écart qui n'existe pas. */
    return <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Lecture de la politique des comptes…</div>;
  }
  return <UsersPanel seuilMois={seuil} />;
}

/**
 * LA POLITIQUE DES COMPTES.
 *
 * ⚠️ DEUX RÉGLAGES QUI NE SE RESSEMBLENT QUE PAR LEUR FORME. Le premier compte
 * en MOIS et ne fait que SIGNALER ; le second compte en MINUTES et DÉCONNECTE.
 * Les ranger côte à côte sans le dire inviterait à croire qu'un compte dormant
 * est fermé — il ne l'est pas, et c'est tout l'intérêt du signalement.
 */
export function OngletPolitiqueComptes() {
  const [mois, setMois] = useState<number | null>(null);
  const [minutes, setMinutes] = useState<number | null>(null);
  const [initial, setInitial] = useState<{ mois: number; minutes: number } | null>(null);
  const [etat, setEtat] = useState<'lecture' | 'pret' | 'envoi'>('lecture');
  const [message, setMessage] = useState('');

  useEffect(() => {
    void (async () => {
      try {
        const r = await fetch('/api/politique', { cache: 'no-store' });
        const d = await r.json();
        if (!r.ok) { setMessage(d.error || 'Lecture refusée.'); return; }
        setMois(Number(d.inactivityMonths)); setMinutes(Number(d.sessionTimeoutMin));
        setInitial({ mois: Number(d.inactivityMonths), minutes: Number(d.sessionTimeoutMin) });
        setEtat('pret');
      } catch {
        setMessage('Le service est injoignable. Aucun réglage n’a été modifié.');
      }
    })();
  }, []);

  if (etat === 'lecture') {
    return <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{message || 'Lecture en cours…'}</div>;
  }

  const modifie = !!initial && (mois !== initial.mois || minutes !== initial.minutes);

  async function enregistrer() {
    setEtat('envoi'); setMessage('');
    try {
      /* ⚠️ `fetchEcriture` et non `fetch` : le témoin d'attente s'allume comme
         partout ailleurs dans la Qualité. C'est la première chose qui change en
         arrivant du site, où ce témoin n'existe pas. */
      const r = await fetchEcriture('/api/politique', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ inactivityMonths: mois, sessionTimeoutMin: minutes }),
      });
      const d = await r.json();
      if (!r.ok) { setMessage(d.error || 'L’enregistrement a échoué.'); return; }
      setInitial({ mois: Number(d.inactivityMonths), minutes: Number(d.sessionTimeoutMin) });
      setMois(Number(d.inactivityMonths)); setMinutes(Number(d.sessionTimeoutMin));
      setMessage('Enregistré.');
    } catch {
      setMessage('Le service est injoignable. Rien n’a été enregistré.');
    } finally { setEtat('pret'); }
  }

  return (
    <div>
      <SectionTitle note={<>
        Un compte sans connexion depuis ce délai est signalé comme <strong>dormant</strong> dans la
        liste des utilisateurs. <strong>Rien n’est fait automatiquement</strong> : c’est un
        signalement, la suspension reste une décision.
      </>}>Comptes dormants</SectionTitle>
      <div style={{ maxWidth: 320 }}>
        <label style={lbl}>Seuil d’inactivité (mois)</label>
        <input type="number" min={1} max={36} value={mois ?? 3}
          onChange={(e) => setMois(Math.min(36, Math.max(1, +e.target.value || 3)))}
          style={{ ...field, fontFamily: 'var(--font-mono)' }} />
        <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }}>
          Par défaut 3 mois. Un compte <strong>jamais connecté</strong> est jugé sur sa date de
          création : une invitation récente n’est pas dormante.
        </div>
      </div>

      <div style={{ marginTop: 30 }}>
        <SectionTitle note={<>
          Les postes du laboratoire sont partagés. Une session oubliée sur une paillasse laisse le
          nom d’une personne sur tout ce qui sera signé ou validé depuis cet écran. Passé ce délai
          <strong> sans aucun geste</strong> (clic, frappe, défilement), un avertissement s’affiche
          pendant une minute avec un bouton <strong>Rester connecté</strong> ; la déconnexion ne
          survient qu’ensuite.
          <br />La déconnexion est <strong>reportée, jamais forcée</strong>, si une saisie est en
          cours : rien de ce qui est à l’écran n’est jeté. <strong>0 désactive</strong> le mécanisme.
        </>}>Déconnexion automatique</SectionTitle>
        <div style={{ maxWidth: 320 }}>
          <label style={lbl}>Inactivité avant déconnexion (minutes)</label>
          {/* ⚠️ `0` est une VALEUR — il éteint le mécanisme. Un `|| 30` le
              transformerait silencieusement en trente minutes, et l'on croirait
              avoir désactivé ce qui tourne toujours. */}
          <input type="number" min={0} max={480} value={minutes ?? 30}
            onChange={(e) => {
              const v = Number(e.target.value);
              setMinutes(Math.min(480, Math.max(0, Number.isFinite(v) ? v : 30)));
            }}
            style={{ ...field, fontFamily: 'var(--font-mono)' }} />
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }}>
            Par défaut 30 minutes. Le réglage vaut pour <strong>toute la suite</strong> — site,
            catalogue et Qualité ; il prend effet au prochain chargement d’écran.
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 26 }}>
        <button onClick={() => void enregistrer()} disabled={!modifie || etat === 'envoi'}
          className="ged-btn"
          style={{ background: 'var(--color-primary)', border: 'none', borderRadius: 'var(--radius-md)', padding: '9px 16px', fontSize: 13, fontWeight: 600, color: 'var(--text-on-primary)', cursor: modifie ? 'pointer' : 'default', opacity: modifie && etat !== 'envoi' ? 1 : 0.55 }}>
          {etat === 'envoi' ? 'Enregistrement…' : 'Enregistrer'}
        </button>
        <span style={{ fontSize: 12.5, color: message === 'Enregistré.' ? 'var(--green-700)' : message ? 'var(--red-600)' : 'var(--text-muted)' }}>
          {message || (modifie ? 'Modifications non enregistrées' : 'Aucune modification')}
        </span>
      </div>

      {/* ⚠️ Ces réglages sont ceux de TOUTE la suite, pas de la seule Qualité :
          le dire évite qu'on les règle ici en croyant ne toucher qu'un module. */}
      <div style={{ ...card, marginTop: 26, padding: '11px 14px', fontSize: 12.5, lineHeight: 1.6, color: 'var(--text-muted)' }}>
        Ces deux réglages sont enregistrés dans le paramétrage <strong>commun</strong> aux trois
        applications, et non dans celui de la Qualité seule. Ils se réglaient auparavant depuis le
        module Site ; ils ont été rapatriés ici le 30 août 2026, avec l’annuaire des comptes qu’ils
        gouvernent.
      </div>
    </div>
  );
}
